/**
 * Multi-tab auth: signs this tab out when another tab logs out or its own
 * session ends, follows another user's login, and runs the idle sign-out of
 * "remember me" unticked logins.
 */

import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { useAuthSync, useSessionEnded } from "@/hooks/useAuthSync";
import { useIdleSignOut } from "@/hooks/useIdleSignOut";
import {
  endLoginEverywhere,
  type IdleSignOutReason,
} from "@/utils/idleSession";
import { IdleSignOutDialog } from "@/components/auth/IdleSignOutDialog";
import { TokenStorage } from "@/api/client";
import { STORAGE_KEYS } from "@/constants";

/** The user id every tab shares (persisted auth state), or null. */
function sharedUserId(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.AUTH_STORAGE);
    return raw ? (JSON.parse(raw)?.state?.user?.id ?? null) : null;
  } catch {
    return null;
  }
}

export const AuthSyncProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    logout,
    endInThisTab,
    isAuthenticated,
    isHydrated,
    serverUnreachable,
  } = useAuthStore();
  const qc = useQueryClient();

  // "Remember me" unticked: sign every tab out after a spell of inactivity.
  // ProtectedRoute then sends this tab to login, with the page to come back
  // to; the login page explains an idle sign-out (a toast would be long gone
  // by the time anyone is back at the screen).
  const signOut = React.useCallback(
    (reason: IdleSignOutReason | "button") => {
      if (reason === "ended") {
        // A login from before the update: end it in this tab only. A tab
        // of the old build may still be open on it, and must not be signed
        // out mid-work.
        endInThisTab();
        qc.clear();
        return;
      }
      // This tab was open as the time ran out: worth explaining on return.
      endLoginEverywhere({ note: reason === "idle" });
      logout();
      qc.clear();
      if (reason === "button") toast.success("Logged out successfully");
    },
    [logout, endInThisTab, qc],
  );

  // A tab restored from the back/forward cache missed every storage event
  // while it was frozen: if the shared login is now someone else's (or
  // nobody's), reload rather than keep showing this user's data.
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const current = useAuthStore.getState().user?.id ?? null;
      if (current && sharedUserId() !== current) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);
  const idle = useIdleSignOut({
    // Not before the boot refresh has settled: a login whose cookie is gone
    // is signed out by that, not reported as idle.
    // Nor under "can't reach the server": that screen shows nobody's login,
    // so moving the mouse there mustn't keep it (maybe someone else's)
    // alive, and no "Are you still there?" over it. The next page load
    // still applies the idle limit.
    enabled: isAuthenticated && isHydrated && !serverUnreachable,
    onSignOut: signOut,
  });

  // This tab's own session ended (refresh refused, or refresh gave up).
  useSessionEnded(() => {
    logout();
    qc.clear();
  });

  useAuthSync({
    onLogout: () => {
      // Another tab logged out - clear our state too
      logout();
      qc.clear();
    },
    onLogin: (data) => {
      // Someone else signed in in another tab: the shared cookie is now
      // theirs. Reload as them rather than keep showing this user's name and
      // data on their login.
      const current = useAuthStore.getState().user?.id;
      if (data?.userId && current && data.userId !== current) {
        qc.clear();
        window.location.reload();
        return;
      }
      // Another tab logged in - follow its "remember me" choice and refresh
      TokenStorage.syncRememberMe();
      qc.invalidateQueries({ queryKey: ["profile"] });
    },
    onSessionExpired: () => {
      // Session expired in another tab
      logout();
      qc.clear();
    },
  });

  return (
    <>
      {children}
      <IdleSignOutDialog
        open={idle.warning}
        secondsLeft={idle.secondsLeft}
        onStay={idle.staySignedIn}
        onSignOut={() => signOut("button")}
      />
    </>
  );
};
